<template>
	<section
		ref="bandElement"
		:aria-label="t('hero.tagline')"
		class="stats"
	>
		<div class="container stats__grid">
			<div
				v-for="(stat, index) in STATS"
				:key="stat.key"
				class="stats__item"
			>
				<div class="stats__value">{{ shownValues[index] }}{{ stat.suffix }}</div>
				<div class="stats__label">{{ t(`stats.${stat.key}`) }}</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { STATS } from '@/helpers/constants'
import { prefersReducedMotion } from '@/helpers/functions'

defineOptions({ name: 'StatsBand' })

const { t } = useI18n()

const COUNT_DURATION = 1100

const bandElement = ref(null)
const shownValues = ref(STATS.map(() => 0))
const hasPlayed = ref(false)

let observer = null

const showFinalValues = () => {
	shownValues.value = STATS.map((stat) => stat.value)
}

const countUp = () => {
	if (hasPlayed.value) return
	hasPlayed.value = true

	if (prefersReducedMotion()) {
		showFinalValues()
		return
	}

	const start = performance.now()

	const step = (now) => {
		const progress = Math.min(1, (now - start) / COUNT_DURATION)
		const eased = 1 - Math.pow(1 - progress, 3)
		shownValues.value = STATS.map((stat) => Math.round(stat.value * eased))
		if (progress < 1) requestAnimationFrame(step)
	}

	requestAnimationFrame(step)
}

onMounted(() => {
	if (!('IntersectionObserver' in window)) {
		countUp()
		return
	}

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return
				observer.disconnect()
				countUp()
			})
		},
		{ threshold: 0.4 }
	)

	observer.observe(bandElement.value)
})

onBeforeUnmount(() => {
	if (observer) observer.disconnect()
})
</script>

<style scoped lang="scss">
.stats {
	position: relative;
	z-index: 3;
	padding: clamp(34px, 5vw, 52px) 0;
	background: $color-ink;
	color: $color-paper;
}

.stats__grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: clamp(16px, 3vw, 34px);
	text-align: center;
}

.stats__item {
	position: relative;
	padding: 6px 4px;

	& + &::before {
		content: '';
		position: absolute;
		inset-inline-start: 0;
		top: 14%;
		height: 72%;
		width: 1px;
		background: rgba(255, 255, 255, 0.14);
	}
}

.stats__value {
	font-size: clamp(2rem, 4.6vw, 3.1rem);
	font-weight: 800;
	line-height: 1;
	letter-spacing: -0.03em;
	color: $color-gold;
	font-variant-numeric: tabular-nums;
}

.stats__label {
	margin-top: 10px;
	font-size: 0.85rem;
	font-weight: 500;
	color: #b9b9b9;
}

@include below($bp-tablet) {
	.stats__grid {
		grid-template-columns: repeat(2, 1fr);
		gap: 22px 10px;
	}

	.stats__item:nth-child(3)::before {
		display: none;
	}
}
</style>
